import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Cpu, Database, FolderArchive, HardDrive, MemoryStick, RefreshCw, Save, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const METRICS = [
  { key: "memory", label: "Memory (RAM)", icon: MemoryStick, unit: "GB" },
  { key: "cpu", label: "CPU", icon: Cpu, unit: "cores" },
  { key: "db", label: "Database", icon: Database, unit: "MB" },
  { key: "files", label: "File storage", icon: FolderArchive, unit: "GB" },
  { key: "disk", label: "Disk (shared server)", icon: HardDrive, unit: "GB" },
];

const barColour = (pct, warn, crit) => {
  if (pct == null) return "bg-muted";
  if (pct >= crit) return "bg-[#C62828]";
  if (pct >= warn) return "bg-[#B8860B]";
  return "bg-[#2E7D32]";
};

export default function ResourceMonitor() {
  const [cfg, setCfg] = useState(null);
  const [readings, setReadings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/settings/resource-monitor");
      setCfg(data.config);
      setReadings(data.readings || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const recipients = (cfg.recipients_text ?? (cfg.recipients || []).join(", "))
        .split(/[,\n;]/)
        .map((e) => e.trim())
        .filter(Boolean);
      const { data } = await api.put("/settings/resource-monitor", {
        enabled: cfg.enabled,
        warn_pct: Number(cfg.warn_pct),
        critical_pct: Number(cfg.critical_pct),
        recipients,
        metrics: cfg.metrics,
      });
      setCfg(data);
      toast.success("Monitoring settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/resource-monitor/test");
      setReadings(data.readings || {});
      toast.success(
        data.email_sent
          ? "Test alert emailed to your recipients"
          : "Checked usage — add a recipient to receive emails"
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading || !cfg)
    return <div className="p-8 text-sm text-muted-foreground" data-testid="monitor-loading">Loading…</div>;

  const warn = Number(cfg.warn_pct);
  const crit = Number(cfg.critical_pct);
  const recipientsText = cfg.recipients_text ?? (cfg.recipients || []).join(", ");
  const alerted = cfg.metrics || [];

  return (
    <div className="fade-up space-y-8" data-testid="resource-monitor-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">System Monitoring</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Watches your live portal's memory, CPU, database and file storage every 15
            minutes and emails you before anything runs out — so you can act ahead of time.
          </p>
        </div>
        <Button
          variant="outline"
          className="h-11 rounded-none"
          onClick={load}
          disabled={refreshing}
          data-testid="monitor-refresh-button"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh now
        </Button>
      </div>

      {/* Live usage */}
      <div className="grid gap-4 sm:grid-cols-3" data-testid="monitor-readings">
        {METRICS.map(({ key, label, icon: Icon, unit }) => {
          const r = readings[key];
          const pct = r?.pct ?? null;
          return (
            <div key={key} className="border border-border p-5" data-testid={`monitor-card-${key}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Icon className="h-4 w-4 text-[#C5A059]" /> {label}
                </div>
                {!alerted.includes(key) && (
                  <span
                    className="border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
                    data-testid={`monitor-infoonly-${key}`}
                  >
                    Info only
                  </span>
                )}
              </div>
              {!r ? (
                <p className="mt-4 text-sm text-muted-foreground">Not available here</p>
              ) : pct == null ? (
                <div className="mt-4">
                  <span className="text-3xl font-bold" data-testid={`monitor-pct-${key}`}>
                    {r.used_label}
                  </span>
                  {r.sub && (
                    <span className="ml-2 text-xs text-muted-foreground">{r.sub}</span>
                  )}
                </div>
              ) : (
                <>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-3xl font-bold" data-testid={`monitor-pct-${key}`}>
                      {pct}%
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {key === "cpu"
                        ? `of ${r.cores} ${unit}`
                        : `${r.used_gb} / ${r.limit_gb} ${unit}`}
                    </span>
                  </div>
                  <div className="mt-3 h-2 w-full bg-muted">
                    <div
                      className={`h-2 ${barColour(pct, warn, crit)}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                  {key === "disk" && (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Shared server disk — your data is stored off-server, so this is shown for
                      information only and never triggers an alert.
                    </p>
                  )}
                  {key === "files" && (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Shared 5 GB pool across all your Emergent apps; uploads can't be deleted yet.
                    </p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Settings */}
      <div className="max-w-2xl space-y-6 border border-border p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Activity className="h-4 w-4 text-[#C5A059]" /> Email alerts
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Turn the watchdog's emails on or off.
            </p>
          </div>
          <Switch
            checked={cfg.enabled}
            onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
            data-testid="monitor-enabled-switch"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium">Warn at (%)</span>
            <Input
              type="number"
              min={1}
              max={99}
              className="mt-1 h-11 rounded-none"
              value={cfg.warn_pct}
              onChange={(e) => setCfg({ ...cfg, warn_pct: e.target.value })}
              data-testid="monitor-warn-input"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Critical at (%)</span>
            <Input
              type="number"
              min={2}
              max={100}
              className="mt-1 h-11 rounded-none"
              value={cfg.critical_pct}
              onChange={(e) => setCfg({ ...cfg, critical_pct: e.target.value })}
              data-testid="monitor-critical-input"
            />
          </label>
        </div>

        <label className="block text-sm">
          <span className="font-medium">Send alerts to</span>
          <Input
            className="mt-1 h-11 rounded-none"
            value={recipientsText}
            onChange={(e) => setCfg({ ...cfg, recipients_text: e.target.value })}
            placeholder="name@example.com, second@example.com"
            data-testid="monitor-recipients-input"
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            Separate multiple addresses with commas.
          </span>
        </label>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            onClick={save}
            disabled={saving}
            className="h-11 rounded-none bg-[#C5A059] px-6 font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
            data-testid="monitor-save-button"
          >
            <Save className="mr-2 h-4 w-4" /> Save settings
          </Button>
          <Button
            variant="outline"
            onClick={sendTest}
            disabled={testing}
            className="h-11 rounded-none"
            data-testid="monitor-test-button"
          >
            <Send className="mr-2 h-4 w-4" /> Send test email
          </Button>
        </div>
      </div>
    </div>
  );
}
